import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

interface Options<S> {
  key: string;
  ttl?: number;
  select?: (state: S) => Partial<S>;
  storage?: Storage;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

function readCache<T>(
  key: string,
  ttl: number,
  storage: Storage
): Partial<T> | undefined {
  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return undefined;
    }
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.timestamp > ttl) {
      storage.removeItem(key);
      return undefined;
    }
    return entry.data;
  } catch {
    return undefined;
  }
}

function writeCache<T>(key: string, data: T, storage: Storage): void {
  try {
    storage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // storage full or unavailable — fail silently
  }
}

const isAction = (action: unknown): action is { type: string } =>
  !!action &&
  typeof action === 'object' &&
  'type' in action &&
  typeof action.type === 'string';

const isActionWithPayload = (
  action: unknown
): action is { type: string; payload: object } =>
  isAction(action) && 'payload' in action && typeof action.payload === 'object';

const extendReducer =
  <S, A>(reducer: (state: S, action: A) => S) =>
  (state: S, action: A): S => {
    if (isActionWithPayload(action) && action.type === 'SYNC_FROM_CACHE') {
      return { ...state, ...action.payload };
    }
    return reducer(state, action);
  };

const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
const TAB_ID = Math.random().toString(36).slice(2, 9);
const BROADCAST_DATA_TYPE = 'airship:CACHE_UPDATE';

export function useReducerStorage<S, A>(
  reducer: (state: S, action: A) => S,
  initState: S,
  options?: Options<S>
): [S, Dispatch<A>] {
  const { key, select, ttl = ONE_WEEK, storage = localStorage } = options || {};

  const hasMountedRef = useRef(false);
  const tabIdRef = useRef(TAB_ID);
  const keyRef = useRef(key);
  const initStateRef = useRef(initState);
  const lastActionRef = useRef<string>(undefined);
  const channelRef = useRef<BroadcastChannel>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const broadcastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const storageRef = useRef(storage);
  const selectRef = useRef(select);

  const extendedReducer = useMemo(() => extendReducer(reducer), [reducer]);

  const [state, dispatch] = useReducer(extendedReducer, initState, (init) => {
    const cached = key ? readCache<S>(key, ttl, storageRef.current) : undefined;
    return cached ? { ...init, ...cached } : init;
  });

  const wrappedDispatch = useCallback((action: A) => {
    if (isAction(action)) {
      lastActionRef.current = action.type;
    }
    dispatch(action);
  }, []);

  const selectedData = useMemo(
    () => (selectRef.current ? selectRef.current(state) : state),
    [state]
  );

  useEffect(() => {
    if (!key) {
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(
      () => writeCache(key, selectedData, storageRef.current),
      500
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [key, selectedData]);

  useEffect(() => {
    if (!key || typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel(`cache-sync:${key}`);
    channelRef.current = channel;

    const onMessage = (event: MessageEvent): void => {
      if (
        event.data.type === BROADCAST_DATA_TYPE &&
        event.data.state &&
        event.data.originTabId !== tabIdRef.current
      ) {
        lastActionRef.current = 'SYNC_FROM_CACHE';
        dispatch({ type: 'SYNC_FROM_CACHE', payload: event.data.state } as A);
      }
    };

    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      channelRef.current = undefined;
    };
  }, [key]);

  useEffect(() => {
    if (!key) {
      return;
    }

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const channel = channelRef.current;
    if (!channel || lastActionRef.current === 'SYNC_FROM_CACHE') {
      lastActionRef.current = undefined;
      return;
    }

    if (broadcastTimeoutRef.current) {
      clearTimeout(broadcastTimeoutRef.current);
    }

    broadcastTimeoutRef.current = setTimeout(() => {
      if (channelRef.current) {
        channelRef.current.postMessage({
          type: BROADCAST_DATA_TYPE,
          state: selectedData,
          originTabId: tabIdRef.current,
        });
      }
    }, 1000);

    return () => {
      if (broadcastTimeoutRef.current) {
        clearTimeout(broadcastTimeoutRef.current);
      }
    };
  }, [key, selectedData]);

  useEffect(() => {
    const prevKey = keyRef.current;
    keyRef.current = key;

    if (!hasMountedRef.current || prevKey === key || !key) {
      return;
    }

    const cached = readCache<S>(key, ttl, storageRef.current);
    dispatch({
      type: 'SYNC_FROM_CACHE',
      payload: { ...initStateRef.current, ...(cached ?? {}) },
    } as A);
  }, [key, ttl]);

  return [state, wrappedDispatch];
}
